"use client"

import * as React from "react"
import { Link } from "react-router"
import { useLocation } from "react-router"
import { ChevronRight, type LucideIcon } from "lucide-react"

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar"

export function NavMain({
  items,
}: {
  items: {
    title: string
    url: string
    icon?: LucideIcon
    isActive?: boolean
    items?: {
      title: string
      url: string
    }[]
  }[]
}) {
  const [openItems, setOpenItems] = React.useState<Record<string, boolean>>({})

  const isExternalUrl = React.useCallback((url: string) => {
    return url.startsWith("http")
  }, [])

  const location = useLocation()

  React.useEffect(() => {
    const normalizedPathname = location.pathname.replace(/\/$/, "")

    setOpenItems((prev) => {
      const next = { ...prev }

      items.forEach((item) => {
        // 1) Grupo marcado como "ativo" por configuração (quando houver).
        if (item.isActive && next[item.title] === undefined) next[item.title] = true

        // 2) Grupo que contém o item atualmente ativo precisa ficar aberto.
        if ((item.items?.length ?? 0) > 1) {
          const shouldOpen = item.items?.some((subItem) => {
            const subUrl = subItem.url.replace(/\/$/, "")
            return (
              normalizedPathname === subUrl ||
              normalizedPathname.startsWith(`${subUrl}/`)
            )
          })

          if (shouldOpen) next[item.title] = true
        }
      })

      return next
    })
  }, [items, location.pathname])

  const handleOpenChange = React.useCallback(
    (title: string) => (open: boolean) => {
      setOpenItems((prev) => ({
        ...prev,
        [title]: open,
      }))
    },
    []
  )

  return (
    <SidebarGroup>
      <SidebarGroupLabel className="text-[11px] leading-4 font-medium text-foreground/80 normal-case border-l-0 pl-0">
        Plataforma
      </SidebarGroupLabel>
      <SidebarMenu>
        {items.map((item) => {
          const hasSubItems = (item.items?.length ?? 0) > 1

          if (!hasSubItems) {
            const targetUrl = item.items?.[0]?.url ?? item.url

            return (
              <SidebarMenuItem key={item.title}>
                <SidebarMenuButton asChild tooltip={item.title}>
                  {isExternalUrl(targetUrl) ? (
                    <a href={targetUrl}>
                      {item.icon && <item.icon />}
                      <span>{item.title}</span>
                    </a>
                  ) : (
                    <Link to={targetUrl}>
                      {item.icon && <item.icon />}
                      <span>{item.title}</span>
                    </Link>
                  )}
                </SidebarMenuButton>
              </SidebarMenuItem>
            )
          }

          return (
            <Collapsible
              key={item.title}
              asChild
              open={Boolean(openItems[item.title])}
              onOpenChange={handleOpenChange(item.title)}
              className="group/collapsible"
            >
              <SidebarMenuItem>
                <CollapsibleTrigger asChild>
                  <SidebarMenuButton tooltip={item.title}>
                    {item.icon && <item.icon />}
                    <span>{item.title}</span>
                    <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                  </SidebarMenuButton>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <SidebarMenuSub>
                    {item.items?.map((subItem) => (
                      <SidebarMenuSubItem key={subItem.title}>
                        <SidebarMenuSubButton asChild>
                          {isExternalUrl(subItem.url) ? (
                            <a href={subItem.url}>
                              <span>{subItem.title}</span>
                            </a>
                          ) : (
                            <Link to={subItem.url}>
                              <span>{subItem.title}</span>
                            </Link>
                          )}
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    ))}
                  </SidebarMenuSub>
                </CollapsibleContent>
              </SidebarMenuItem>
            </Collapsible>
          )
        })}
      </SidebarMenu>
    </SidebarGroup>
  )
}
